/**
 * Limite de erro de renderização.
 *
 * Sem isto, qualquer exceção durante o render desmonta a árvore inteira e o
 * usuário vê uma página branca — sem mensagem, sem caminho de volta. O React
 * não tem versão em hook disso; precisa ser classe.
 *
 * Em desenvolvimento mostra a mensagem e a stack, porque é o que faz o
 * diagnóstico ser imediato. Em produção mostra só o essencial, para não expor
 * caminho de arquivo nem estrutura interna a quem estiver na tela.
 */

import { AlertTriangle } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '@/components/ui';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    console.error('Erro de renderização:', error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null, componentStack: null });
  };

  override render(): ReactNode {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-6">
        <div className="w-full max-w-2xl">
          <div className="card p-6">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-bad-soft">
                <AlertTriangle className="h-5 w-5 text-bad" aria-hidden />
              </span>

              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-bold">Algo quebrou nesta tela</h1>
                <p className="mt-1 text-sm text-ink-2">
                  O restante do aplicativo continua funcionando. Você pode tentar de novo ou
                  voltar ao início.
                </p>

                {import.meta.env.DEV ? (
                  <pre className="mt-4 max-h-64 overflow-auto rounded-lg bg-sunken p-3 text-[12px] leading-relaxed text-bad">
                    {error.message}
                    {componentStack ? `\n${componentStack}` : ''}
                  </pre>
                ) : null}

                <div className="mt-5 flex gap-2">
                  <Button onClick={this.reset}>Tentar de novo</Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      window.location.href = '/';
                    }}
                  >
                    Voltar ao início
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
